import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Coins, Trophy, Package } from 'lucide-react';
import { getLocalizedText } from './MultiLanguageInput';
import { priceToRarity } from './landing';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const resolveImg = (url) => (url && url.startsWith('/api') ? `${BACKEND}${url}` : url);

/* ---------------- Banner Slider (dynamic from DB) ---------------- */
export const BannerSlider = ({ banners = [], onNavigate, testid, interval = 4500 }) => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const count = banners.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const t = setInterval(() => { setDir(1); setIdx((i) => (i + 1) % count); }, interval);
    return () => clearInterval(t);
  }, [count, interval]);

  const go = (n) => { setDir(n > idx ? 1 : -1); setIdx((n + count) % count); };

  if (count === 0) {
    return (
      <div data-testid={testid} className="w-full h-44 sm:h-52 rounded-3xl border border-[#F0E3C8] bg-[#FFF8EA] flex items-center justify-center">
        <span className="text-[#9A8F79] text-sm font-semibold">No banners</span>
      </div>
    );
  }

  const current = banners[idx];

  return (
    <div data-testid={testid} className="group relative w-full h-44 sm:h-52 overflow-hidden rounded-3xl border border-[#F0E3C8] bg-white shadow-[0_12px_40px_-18px_rgba(255,140,0,0.45)]">
      <AnimatePresence initial={false} custom={dir} mode="popLayout">
        <motion.button
          key={current.id || idx}
          custom={dir}
          initial={{ opacity: 0, x: dir * 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: dir * -60 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          onClick={() => onNavigate && onNavigate(current)}
          data-testid={`${testid}-slide`}
          className="absolute inset-0 w-full h-full"
        >
          <img src={current.image_url} alt={current.title || ''} className="w-full h-full object-cover" />
        </motion.button>
      </AnimatePresence>

      {count > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(idx - 1); }} data-testid={`${testid}-prev`}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-[#1A1206] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
            <ChevronLeft size={18} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(idx + 1); }} data-testid={`${testid}-next`}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-[#1A1206] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
            {banners.map((b, i) => (
              <button key={b.id || i} onClick={(e) => { e.stopPropagation(); go(i); }} data-testid={`${testid}-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-[#FF8C00]' : 'w-1.5 bg-white/70 hover:bg-white'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const GRADE = {
  Mythic: 'GEM MT 10',
  Legendary: 'GEM MT 10',
  Epic: 'MINT 9',
  Rare: 'NM-MT 8',
  Common: 'NM 7',
};
const GRADE_COLOR = {
  Mythic: '#FF5F00',
  Legendary: '#FF8C00',
  Epic: '#FFA500',
  Rare: '#FFB300',
  Common: '#C9A227',
};
export const gradeFor = (rarity) => GRADE[rarity] || 'NM 7';

export const usd = (n) => `$${Number(n || 0).toLocaleString('en-US')}`;

/* ---------------- Pack/Rips Banner ---------------- */
export const PackBanner = ({ label, sub, images = [], heroImage, accent = '#FF8C00', onClick, testid }) => (
  <motion.button
    whileHover={{ y: -4 }} onClick={onClick} data-testid={testid}
    className="group relative w-full overflow-hidden rounded-3xl border border-[#F0E3C8] bg-white shadow-[0_12px_40px_-18px_rgba(255,140,0,0.45)] hover:border-[#FF8C00] transition-all"
  >
    <div className="relative h-44 sm:h-52 flex items-center">
      {heroImage ? (
        /* single hero image on the right */
        <div className="absolute inset-y-0 right-0 w-[58%] flex items-center justify-end pr-3">
          <img
            src={heroImage}
            alt={label}
            data-testid={`${testid}-hero-image`}
            className="h-[92%] w-auto object-contain drop-shadow-[0_10px_24px_rgba(0,0,0,0.35)] group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      ) : (
        /* collage */
        <div className="absolute inset-0 flex items-center justify-end gap-2 pr-4 opacity-90">
          {images.slice(0, 4).map((src, i) => (
            <img key={i} src={src} alt="" className="h-[78%] w-auto object-cover rounded-xl shadow-lg rotate-[-4deg] group-hover:rotate-0 transition-transform duration-500"
              style={{ transform: `translateY(${(i % 2) * 10}px) rotate(${i % 2 ? 6 : -6}deg)` }} />
          ))}
        </div>
      )}
      <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, #FFFFFF 28%, rgba(255,255,255,0.6) 50%, transparent 80%)` }} />
      <div className="relative pl-6 sm:pl-8">
        <span className="inline-block px-4 py-1.5 rounded-full text-[#1A1206] font-black uppercase tracking-wide font-['Unbounded'] text-2xl sm:text-3xl" style={{ background: accent, boxShadow: `0 0 24px ${accent}88` }}>
          {label}
        </span>
        {sub && <p className="mt-3 text-[#6E6452] text-sm font-semibold max-w-[14rem]">{sub}</p>}
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#FF6A00]">Browse <ChevronRight size={15} /></span>
      </div>
    </div>
  </motion.button>
);

/* ---------------- Chase Card (iced-glass / glassmorphism) ---------------- */
export const ChaseCard = ({ index, title, price, rarity, image, remaining = 0, total = 0, onClick }) => {
  const grade = gradeFor(rarity);
  const gc = GRADE_COLOR[rarity] || '#64748B';
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }}
      transition={{ delay: Math.min(index * 0.03, 0.25) }} whileHover={{ y: -6 }}
      data-testid={`chase-card-${index}`}
      className="group relative flex flex-col rounded-2xl overflow-hidden border border-white/60 bg-white/25 backdrop-blur-xl shadow-[0_10px_30px_-12px_rgba(0,0,0,0.3)] transition-all duration-300 hover:border-white/90 hover:shadow-[0_20px_46px_-14px_rgba(255,140,0,0.6)]"
      style={{ WebkitBackdropFilter: 'blur(16px)' }}
    >
      {/* Glass sheen overlays */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-br from-white/50 via-transparent to-white/10" />
      <div aria-hidden className="pointer-events-none absolute -top-1/4 -left-1/3 w-2/3 h-2/3 rotate-[18deg] bg-white/30 blur-2xl z-20" />
      <div aria-hidden className="pointer-events-none absolute inset-0 z-20 rounded-2xl ring-1 ring-inset ring-white/40" />

      <div onClick={onClick} className="relative z-10 m-1.5 rounded-xl aspect-[3/4] overflow-hidden bg-[#FFF8EA]/70 cursor-pointer border border-white/50">
        {image && <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        <span className="absolute top-2 right-2 z-30 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md text-white shadow" style={{ background: gc }}>{grade}</span>
      </div>
      <div className="relative z-30 px-3 pb-3 pt-1 flex flex-col gap-2">
        <h3 onClick={onClick} className="text-[#1A1206] font-bold text-xs leading-tight line-clamp-1 uppercase tracking-wide cursor-pointer drop-shadow-[0_1px_1px_rgba(255,255,255,0.6)]">{title}</h3>
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-[#6E6452] font-semibold" data-testid={`chase-stock-${index}`}>
            <Package size={13} className="text-[#7A5C00]" /> {remaining}/{total}
          </span>
          <span className="flex items-center gap-1 font-black text-[#FF6A00] font-['Unbounded'] text-sm">
            <Coins size={14} className="text-[#FFB300]" /> {price}
          </span>
        </div>
        <button onClick={onClick} data-testid={`rip-now-${index}`} className="w-full h-9 rounded-lg font-extrabold text-[#1A1206] bg-gradient-to-r from-[#FFD400] to-[#FF8C00] hover:brightness-105 active:scale-[0.98] transition-all text-xs uppercase tracking-wide shadow-[0_4px_12px_-4px_rgba(255,140,0,0.6)]">
          Rip Now
        </button>
      </div>
    </motion.div>
  );
};

/* ---------------- Winners Ticker (floating bottom) ---------------- */
const WINNERS = [
  { user: 'CryptoKing', card: 'Charizard VMAX (PSA 10)', coins: 42000, rarity: 'Mythic' },
  { user: 'SolWhale', card: 'Pikachu Illustrator', coins: 88000, rarity: 'Mythic' },
  { user: '0xNeo', card: 'Mewtwo GX Gold', coins: 15600, rarity: 'Legendary' },
  { user: 'GachaQueen', card: 'Rayquaza EX Alt Art', coins: 21000, rarity: 'Legendary' },
  { user: 'LuckyDraw88', card: 'Lugia V Full Art', coins: 9800, rarity: 'Epic' },
  { user: 'MoonBoy', card: 'Umbreon VMAX Moonbreon', coins: 33500, rarity: 'Mythic' },
  { user: 'DegenApe', card: 'Blastoise GX Gold', coins: 12400, rarity: 'Legendary' },
  { user: 'VaultHunter', card: 'Giratina V Alt Art', coins: 18700, rarity: 'Epic' },
  { user: 'PackRipper', card: 'Gengar VMAX Alt', coins: 27300, rarity: 'Legendary' },
  { user: 'NeonSamurai', card: 'One Piece Luffy Manga Rare', coins: 31200, rarity: 'Mythic' },
];

export const WinnersTicker = () => {
  const row = [...WINNERS, ...WINNERS];
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 h-11 bg-[#1A1206] overflow-hidden flex items-center border-t-2 border-[#FFD400]" data-testid="winners-ticker">
      <div className="flex items-center gap-1.5 px-4 h-full bg-gradient-to-r from-[#FFD400] to-[#FF8C00] text-[#1A1206] font-black text-xs uppercase tracking-wider shrink-0 z-10">
        <Trophy size={14} /> <span className="hidden sm:inline">Winners</span>
      </div>
      <div className="gp-marquee-track">
        {row.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-6 whitespace-nowrap text-sm">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: GRADE_COLOR[w.rarity] }} />
            <span className="font-bold text-[#FFD400]">{w.user}</span>
            <span className="text-white/50">won</span>
            <span className="font-semibold text-white">{w.card}</span>
            <span className="flex items-center gap-1 text-[#FFB300] font-bold"><Coins size={13} /> {w.coins.toLocaleString()}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Section Heading ---------------- */
export const SectionHead = ({ title, control }) => (
  <div className="flex items-center justify-between mb-5">
    <h2 className="text-xl sm:text-2xl font-black tracking-tight uppercase text-[#1A1206] font-['Unbounded']">{title}</h2>
    {control}
  </div>
);

/* ---------------- Category Section (grouped by subcategory) ---------------- */
const poolToCard = (p, lang) => ({
  id: p.id,
  title: getLocalizedText(p.name, lang),
  price: p.price,
  rarity: priceToRarity(p.price || 0),
  image: p.image_url,
  remaining: p.remaining_quantity || 0,
  total: p.total_quantity || 0,
});

const sortPools = (pools, sortDesc) =>
  [...pools].sort((a, b) => (sortDesc ? (b.price || 0) - (a.price || 0) : (a.price || 0) - (b.price || 0)));

const PoolGrid = ({ pools, sortDesc, lang, onPool }) => (
  <div className="flex gap-3 lg:gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x">
    {sortPools(pools, sortDesc).map((p, i) => {
      const c = poolToCard(p, lang);
      return (
        <div key={c.id} className="shrink-0 w-[150px] sm:w-[185px] lg:w-[205px] snap-start">
          <ChaseCard index={i} {...c} onClick={() => onPool(c.id)} />
        </div>
      );
    })}
  </div>
);

const SubcategoryRow = ({ sub, pools, sortDesc, lang, onPool }) => (
  <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-8" data-testid={`subcategory-row-${sub.slug || sub.id}`}>
    {/* Left image */}
    <div className="w-full lg:w-64 shrink-0">
      <div className="relative rounded-2xl overflow-hidden border border-[#F0E3C8] h-40 lg:h-full lg:min-h-[220px] bg-[#FFF8EA]">
        {sub.image_url ? (
          <img src={resolveImg(sub.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white font-black uppercase text-lg leading-tight font-['Unbounded'] drop-shadow">{getLocalizedText(sub.name, lang)}</h3>
          <span className="text-white/80 text-xs font-semibold">{pools.length} pack{pools.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
    <PoolGrid pools={pools} sortDesc={sortDesc} lang={lang} onPool={onPool} />
  </div>
);

const SubFilterBtn = ({ active, image, label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`shrink-0 flex items-center gap-2 h-12 pr-4 rounded-full border font-bold text-sm transition-all ${image ? 'pl-1' : 'pl-4'} ${active ? 'bg-[#FF8C00] border-[#FF8C00] text-white shadow-[0_6px_18px_-6px_rgba(255,140,0,0.7)]' : 'bg-white border-[#F0E3C8] text-[#1A1206] hover:border-[#FF8C00]'}`}
  >
    {image && <img src={resolveImg(image)} alt="" className="h-9 w-auto max-w-[96px] object-contain rounded-md" />}
    <span className="whitespace-nowrap">{label}</span>
  </button>
);

export const CategorySection = ({ section, sortDesc, lang, onPool }) => {
  const [active, setActive] = useState('all');
  const hasSubs = section.subGroups.length > 0;
  const allPools = [...section.subGroups.flatMap((g) => g.pools), ...section.noSub];
  const shown = active === 'all'
    ? allPools
    : (section.subGroups.find((g) => g.sub.id === active)?.pools || []);

  return (
    <section className="mt-8" data-testid={`category-section-${section.id}`}>
      {/* Full-width amber band: category title -> subcategory filters */}
      <div className="w-full bg-[#FFB300]">
        <div className="max-w-[1500px] mx-auto px-4 lg:px-8 py-5">
          <div className="flex items-center gap-3">
            {section.icon && (
              <img src={resolveImg(section.icon)} alt="" className="w-11 h-11 rounded-xl object-cover border border-white/60 bg-white p-0.5" />
            )}
            <h2 className="text-xl sm:text-2xl font-black uppercase text-[#1A1206] font-['Unbounded']">{section.name}</h2>
            <span className="text-sm text-[#7A5C00] font-bold">{allPools.length} packs</span>
          </div>

          {hasSubs && (
            <div className="flex gap-2.5 overflow-x-auto pt-4 scrollbar-hide" data-testid={`subfilter-bar-${section.id}`}>
              <SubFilterBtn active={active === 'all'} label="All Packs" onClick={() => setActive('all')} testid={`subfilter-${section.id}-all`} />
              {section.subGroups.map((g) => (
                <SubFilterBtn
                  key={g.sub.id}
                  active={active === g.sub.id}
                  image={g.sub.image_url}
                  label={getLocalizedText(g.sub.name, lang)}
                  onClick={() => setActive(g.sub.id)}
                  testid={`subfilter-${section.id}-${g.sub.slug || g.sub.id}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pool slider (1 row) on base background */}
      <div className="max-w-[1500px] mx-auto px-4 lg:px-8 py-6">
        {shown.length > 0 ? (
          <PoolGrid pools={shown} sortDesc={sortDesc} lang={lang} onPool={onPool} />
        ) : (
          <p className="text-[#7A5C00] text-sm py-6">No packs in this subcategory.</p>
        )}
      </div>
    </section>
  );
};
